import React, { useMemo } from 'react';
import ComposableTabsContext from './ComposableTabsContext';
import ComposableTabsList from './ComposableTabsList';
import ComposableTabsTab from './ComposableTabsTab';
import ComposableTabsPanel from './ComposableTabsPanel';
import { ComponentIdProducer } from './utils';

type ComposableTabsProps = {
  /** Currently active tab, that defines which Tab is shown as active */
  activeTab: string;
  /** Called when user click Tab or navigates tabs using keyboard */
  onActiveTabChange?: (activeTab: string) => void | Promise<void>;
};

const createTabsId = ComponentIdProducer.createStaticProducer({
  prefix: 'telia-components-tabs',
});
/** ComposableTabs is context provider for ComposableTabsList, ComposableTabsTab and ComposableTabsPanel component */
const ComposableTabs: React.FunctionComponent<ComposableTabsProps> & {
  List: typeof ComposableTabsList;
  Tab: typeof ComposableTabsTab;
  Panel: typeof ComposableTabsPanel;
} = ({ children, activeTab, onActiveTabChange }) => {
  const tabsId = useMemo(createTabsId, []);

  const ctxValue = useMemo(() => {
    return {
      tabsId,
      activeTab,
      onActiveTabChange,
    };
  }, [tabsId, onActiveTabChange, activeTab]);

  return (
    <ComposableTabsContext.Provider value={ctxValue}>
      {children}
    </ComposableTabsContext.Provider>
  );
};

ComposableTabs.List = ComposableTabsList;
ComposableTabs.Tab = ComposableTabsTab;
ComposableTabs.Panel = ComposableTabsPanel;

export default ComposableTabs;
